import { validatePassword } from '@components-member/PasswordStrengthMeter/passwordStrength.validation.service';
import { REGEX_CONSTANTS } from '@components-shared/constants';
import { EMAIL_VALIDATION_PATTERN } from '@components-shared/forms/validators';
import { keyMap } from '@locales/lokalise/keyMap';
import * as yup from 'yup';
import { AnyObject, Maybe, StringSchema } from 'yup';

class YupValidationService {
  private validators: Validators;

  constructor(validators: Validators) {
    this.validators = validators;
  }

  public initializeValidationMessages() {
    yup.setLocale({
      mixed: {
        required: keyMap.formsValidationRequiredError,
      },
    });
  }

  public createNullableRequiredStringScheme<T extends string>() {
    return yup
      .string<T>()
      .transform((v) => v ?? '')
      .required()
      .nullable()
      .defined();
  }

  public createNullableNotRequiredStringScheme<T extends string>() {
    return yup
      .string<T>()
      .transform((v) => v ?? '')
      .nullable()
      .defined();
  }

  public createConditionalStringScheme<T>(
    dependentFieldName: string | string[],
    condition: boolean | ((val: T) => boolean),
    schemaIfTrue: (schema: StringSchema) => StringSchema<Maybe<string | undefined>, AnyObject, undefined, ''>,
    schemaIfFalse: (schema: StringSchema) => StringSchema<Maybe<string | undefined>, AnyObject, undefined, ''>,
  ) {
    return yup.string().when(dependentFieldName, {
      is: condition,
      then: schemaIfTrue,
      otherwise: schemaIfFalse,
    });
  }

  public createEmailValidationScheme = (emailPattern: RegExp) => {
    return yup.string().test('check-email', keyMap.formsValidationEmailError, (value) => {
      if (value) {
        return emailPattern.test(value.toLowerCase());
      }
    });
  };

  public createNotRequiredNullableStringScheme() {
    return yup.string().nullable().default('');
  }

  public createFileScheme() {
    return yup
      .mixed<File>()
      .nullable()
      .test('file-test', keyMap.formsValidationRequiredError, (file) => Boolean(file))
      .defined();
  }

  validateOnlyEnglishLetters(value: string) {
    return this.validators.isValidEnglishLetters(value);
  }

  validateOnlyEngishLettersAndDigits(value: string) {
    return this.validators.isValidEnglishLettersAndDigits(value);
  }

  validateEmail(value: string) {
    return this.validators.isValidEmail(value);
  }

  validatPhoneNumberLength(length: number, value: string) {
    return this.validators.isPhoneNumberLengthMoreThan(length, value);
  }

  createPasswordGridFieldSchema() {
    const validatorPassword = validatePassword();

    return yup
      .string()
      .required()
      .test({
        name: 'check-password',
        test(value: string) {
          const { createError, path } = this;
          const isValid = validatorPassword.check(value);

          return isValid
            ? true
            : createError({
                message: `${validatorPassword.message}`,
                path,
              });
        },
      });
  }
}

class Validators {
  isValidEnglishLetters(value: string) {
    const regexPattern = REGEX_CONSTANTS.ONLY_ENG_LETTERS_WITH_SPACE_PATTERN;

    return new RegExp(regexPattern).test(value);
  }

  isValidEnglishLettersAndDigits(value: string) {
    const regexPattern = REGEX_CONSTANTS.ONLY_ENG_AND_DIGITS_PATTERN;

    return new RegExp(regexPattern).test(value);
  }

  isValidEmail(value: string) {
    const regexPattern = EMAIL_VALIDATION_PATTERN;

    return new RegExp(regexPattern).test(value.toLocaleLowerCase());
  }
  isPhoneNumberLengthMoreThan(length: number, value: string) {
    return value.toString().replace(/\D+/g, '').length > length;
  }
}

export const yupValidationService = new YupValidationService(new Validators());
